import _ from "lodash";

import { formatPracticeName } from "../../utility/formatters.js";
import { budgetCostKey, budgetLineItemLabel, budgetLineItemStoredName } from "./budgetLineItems.js";

// Pure Budget Master row logic. Keep JSX, the toolkit, and Vite aliases out so node can run the self-check.

const NO_TYPE = "No Type Specified";

/** Farm vault chip style: Cotton NI. Type stays when it isn't "No Type Specified" so Grain/Silage still differ. */
const cropLabelOf = (cp) =>
  [
    cp.commodity_name,
    cp.type_name === NO_TYPE ? null : cp.type_name,
    formatPracticeName(cp.practice_name) || cp.practice_name,
  ]
    .filter(Boolean)
    .join(" ");

export const idKey = (id) => String(id).toLowerCase();
// Line names never include "(direct)" / "(indirect)"; keys and labels match (`chemical_insecticide_herbicide` = `Chemicals`).
const costKey = (category, name) => budgetCostKey(category, name);
const practiceCostKey = (practiceId, category, name) => `${idKey(practiceId)}::${costKey(category, name)}`;
/** Whether two costs ({ category, name }) are the same cost. */
export const isSameCost = (a, b) => Boolean(a && b) && costKey(a.category, a.name) === costKey(b.category, b.name);
// Direct then indirect, then by name.
const COST_ORDER = [(li) => (li.category === "indirect" ? 1 : 0), (li) => li.name?.toLowerCase()];
export const num = (value) => Number(value) || 0;
const roundCents = (value) => Number(value.toFixed(2));

/** A schedule item's $/acre, unrounded, as BudgetSchedulePopover computes it. */
export const scheduleItemValue = (si) => num(si.quantity) * num(si.unit_value);
// Summed, then rounded once, like BudgetMaster_Update.
export const scheduleTotal = (scheduleItems) => roundCents(_.sumBy(scheduleItems, scheduleItemValue));

/** Joins the BudgetMaster search response into one row per county practice, with its costs and schedule items. */
export function buildBudgetMasterRows(response) {
  const lineItemsByPractice = _.groupBy(response?.line_items ?? [], (li) => idKey(li.budget_county_practice_id));
  const scheduleItemsByPractice = _.groupBy(response?.schedule_items ?? [], (si) =>
    idKey(si.budget_county_practice_id)
  );

  return (response?.county_practices ?? []).map((cp) => {
    const scheduleItems = scheduleItemsByPractice[idKey(cp.id)] ?? [];
    const lineItems = _.sortBy(lineItemsByPractice[idKey(cp.id)] ?? [], COST_ORDER).map((li) => {
      // Spelled like their cost: the schedule popover matches names exactly.
      const costScheduleItems = scheduleItemsForCost(scheduleItems, li).map((si) => ({
        ...si,
        category: li.category,
        line_item_name: li.name,
      }));
      const isScheduled = costScheduleItems.some((si) => si.is_active !== false);
      // A schedule-driven cost is its schedule total, whatever is stored: some stored values are stale.
      const value = isScheduled ? scheduleTotal(costScheduleItems.filter((si) => si.is_active !== false)) : li.value;
      return { ...li, value, scheduleItems: costScheduleItems, isScheduled };
    });

    // Same per-acre (per-head) revenue as BudgetCountyPractice_Search's revenue_simple / acres.
    const revenuePerAcre = cp.is_livestock
      ? (num(cp.weight_out) * num(cp.projected_price)) / 100
      : num(cp.yield) * num(cp.projected_price);
    const costPerAcre = _.sumBy(lineItems, (li) => num(li.value));

    return {
      ...cp,
      countyLabel: `${cp.county_name}, ${cp.state_abbreviation || cp.state_name}`,
      cropLabel: cropLabelOf(cp),
      lineItems,
      scheduleItems,
      revenuePerAcre: roundCents(revenuePerAcre),
      costPerAcre: roundCents(costPerAcre),
      netPerAcre: roundCents(revenuePerAcre - costPerAcre),
    };
  });
}

const sortedUnique = (values) => _.uniq(values).sort((a, b) => a.localeCompare(b));

/** Filter choices built from the loaded rows. */
export function getFilterOptions(rows) {
  const budgets = _.sortBy(
    _.uniqBy(rows, "budget_id").map((row) => ({
      id: row.budget_id,
      label: `${row.budget_name} (${row.budget_status})`,
      status: row.budget_status,
    })),
    (budget) => budget.label.toLowerCase()
  );
  return {
    budgets,
    activeBudgetIds: budgets.filter((budget) => budget.status?.toLowerCase() === "active").map((budget) => budget.id),
    counties: sortedUnique(rows.map((row) => row.countyLabel)),
    crops: sortedUnique(rows.map((row) => row.commodity_name)),
  };
}

/**
 * The Costs screen's picker: the given costs (the defaults) first, then every other cost in the rows, then the picked
 * cost if no row has it yet. A cost is a category and name, listed once in any case.
 */
export function getCostOptions(rows, firstCosts, picked) {
  const otherCosts = _.sortBy(
    rows.flatMap((row) => row.lineItems),
    COST_ORDER
  );
  return _.uniqBy(
    _.compact([...firstCosts, ...otherCosts, picked]).map((cost) => ({
      category: cost.category,
      name: budgetLineItemStoredName(cost.name),
    })),
    (cost) => costKey(cost.category, cost.name)
  );
}

/** Active items for the cost, or the inactive ones when that is all the cost has (Obion chemicals). */
const scheduleItemsForCost = (items, cost) => {
  const matched = (items ?? []).filter((si) =>
    isSameCost({ category: si.category, name: si.line_item_name || si.category }, cost)
  );
  const active = matched.filter((si) => si.is_active !== false);
  return active.length ? active : matched;
};

const scheduleOfCost = (row, cost) => scheduleItemsForCost(row.scheduleItems, cost);

const toCostRow = (row, lineItem, picked, costRowKey) => {
  const cost = lineItem ?? {
    budget_county_practice_id: row.id,
    category: picked.category,
    name: picked.name,
    scheduleItems: [],
    isScheduled: false,
  };
  const fromLine = lineItem?.scheduleItems ?? [];
  const seen = new Set(fromLine.map((si) => idKey(si.id)));
  const scheduleItems = [
    ...fromLine,
    ...scheduleOfCost(row, cost)
      .filter((si) => !seen.has(idKey(si.id)))
      .map((si) => ({ ...si, category: cost.category, line_item_name: cost.name })),
  ];
  const isScheduled = scheduleItems.some((si) => si.is_active !== false);
  const costValue = isScheduled
    ? scheduleTotal(scheduleItems.filter((si) => si.is_active !== false))
    : lineItem
      ? num(lineItem.value)
      : null;
  return {
    ...row,
    cost: { ...cost, scheduleItems, isScheduled, value: costValue },
    costLabel: budgetLineItemLabel(cost.name),
    costValue,
    costTotal: costValue == null ? null : roundCents(costValue * num(row.reported_acreage)),
    ...(costRowKey ? { costRowKey } : {}),
  };
};

const lineForCost = (row, cost) => {
  const matches = row.lineItems.filter((li) => isSameCost(li, cost));
  return matches.find((li) => li.isScheduled) ?? matches.find((li) => Number(li.value)) ?? matches[0];
};

/**
 * Costs screen rows: every county/crop with the picked cost as `cost`. A row without that cost gets an unsaved one.
 * No picked cost: one row per cost that exists (the Cost picker lists the full BUDGET_ITEMS map).
 */
export function buildCostRows(rows, picked) {
  if (!picked) {
    return rows.flatMap((row) =>
      row.lineItems.length
        ? row.lineItems.map((lineItem) =>
            toCostRow(row, lineItem, lineItem, `${idKey(row.id)}::${costKey(lineItem.category, lineItem.name)}`)
          )
        : [toCostRow(row, null, { category: "", name: "" }, `${idKey(row.id)}::`)]
    );
  }
  return rows.map((row) => toCostRow(row, lineForCost(row, picked), picked));
}

/** Grid / expand / checkbox key: unique per cost when the Costs filter is All. */
export const masterKey = (row) => row.costRowKey ?? row.id;

const DEFAULT_SORT = ["budget_name", "countyLabel", "commodity_name", "type_name", "practice_name"];
const CROP_SORT = ["commodity_name", "type_name", "practice_name"];

const compareField = (field, direction) => (a, b) => {
  const x = a[field] ?? null;
  const y = b[field] ?? null;
  if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
  return direction * (typeof x === "string" ? x.localeCompare(y) : x - y);
};

/** Sorts by one grid column (a MUI sort item), nulls last, then by budget, county, crop, type, and practice. */
export function sortBudgetMasterRows(rows, sortItem) {
  const direction = sortItem?.sort === "desc" ? -1 : 1;
  const fields = !sortItem?.field ? [] : sortItem.field === "cropLabel" ? CROP_SORT : [sortItem.field];
  const comparators = [
    ...fields.map((field) => compareField(field, direction)),
    ...DEFAULT_SORT.map((field) => compareField(field, 1)),
  ];
  return [...rows].sort((a, b) => comparators.reduce((result, compare) => result || compare(a, b), 0));
}

/** Grid rows: each master, followed by its children when it is expanded. */
export function flattenMasterChildRows(masters, expandedIds, getChildren) {
  return masters.flatMap((master) => [
    { ...master, rowId: `master:${masterKey(master)}`, kind: "master" },
    ...(expandedIds.has(masterKey(master))
      ? getChildren(master).map((child) => ({ ...child, rowId: `child:${child.id}`, kind: "child", parent: master }))
      : []),
  ]);
}

/** An empty selection means "All". */
export function filterBudgetMasterRows(rows, { budgetIds = [], counties = [], crops = [] }) {
  return rows.filter(
    (row) =>
      (!budgetIds.length || budgetIds.includes(row.budget_id)) &&
      (!counties.length || counties.includes(row.countyLabel)) &&
      (!crops.length || crops.includes(row.commodity_name))
  );
}

// Saves (POST budget/BudgetMaster). Each array is optional; rows that aren't sent are left as they are.

const PRACTICE_FIELDS = ["reported_acreage", "yield", "production_share_percent"];
const LINE_ITEM_FIELDS = ["id", "budget_county_practice_id", "category", "name", "value", "is_active"];
const SCHEDULE_ITEM_FIELDS = [
  "id",
  "budget_county_practice_id",
  "category",
  "line_item_name",
  "ordinal",
  "name",
  "quantity",
  "unit_of_measure",
  "case_quantity",
  "case_unit_of_measure",
  "unit_value",
  "value",
  "is_active",
  "input_share_percent",
];
// The popover's inputs give numeric strings, and "" when cleared; its totals count "" as 0.
export const toScheduleItem = (si) => ({
  ..._.pick(si, SCHEDULE_ITEM_FIELDS),
  line_item_name: budgetLineItemStoredName(si.line_item_name),
  ..._.mapValues(_.pick(si, ["quantity", "case_quantity", "unit_value", "value"]), num),
});

/** Livestock acres and yield come from head and weight out, and a schedule-driven cost from its schedule. */
export function isBudgetMasterCellEditable({ row, field }) {
  if (row.kind === "master") {
    return (
      field === "production_share_percent" || (!row.is_livestock && (field === "reported_acreage" || field === "yield"))
    );
  }
  return field === "costPerAcre" && !row.isScheduled;
}

/** A Costs screen row's children: the cost's schedule items, then "+ add item". */
export const getCostChildren = (row) => [
  ...row.cost.scheduleItems,
  { id: `add-item:${masterKey(row)}`, isAddItem: true },
];

/**
 * The Costs screen edits a plain or missing cost's $/acre, and its schedule items. An item's name is edited in the
 * Budget column.
 */
export function isCostsCellEditable({ row, field }) {
  if (row.kind === "master") return field === "costValue" && !row.cost.isScheduled;
  return !row.isAddItem && ["budget_name", "quantity", "unit_of_measure", "unit_value"].includes(field);
}

/** The save for one committed cell: a county/crop's changed fields, or a cost's $/acre. Null when nothing changed. */
export function getEditSave(row, oldRow) {
  if (row.kind === "master") {
    const changed = PRACTICE_FIELDS.filter((field) => row[field] !== oldRow[field]);
    return changed.length ? { BudgetCountyPractices: [_.pick(row, ["id", ...changed])] } : null;
  }
  return row.value === oldRow.value
    ? null
    : { BudgetCountyPracticeLineItems: [{ ..._.pick(row, LINE_ITEM_FIELDS), name: budgetLineItemStoredName(row.name) }] };
}

// A cost the row lacks has no id until it is saved.
const toLineItem = (cost, newId) => ({
  ..._.pick(cost, LINE_ITEM_FIELDS),
  id: cost.id ?? newId(),
  name: budgetLineItemStoredName(cost.name),
  is_active: true,
});

/**
 * A row's orphan schedule items for `cost` (active, with its category and name in any case, while the row lacks the
 * cost), inactive. A save that creates the cost sends them too, or BudgetMaster_Update totals them into it.
 */
export const getOrphanScheduleItems = (row, cost) =>
  row.lineItems.some((li) => isSameCost(li, cost))
    ? []
    : row.scheduleItems
        .filter((si) => si.is_active !== false && isSameCost({ ...si, name: si.line_item_name }, cost))
        .map((si) => ({ ...toScheduleItem(si), is_active: false }));

const SCHEDULE_EDIT_FIELDS = ["name", "quantity", "unit_of_measure", "unit_value"];

/**
 * The Costs screen's save for one committed cell: the cost's $/acre (creating the cost where the row lacks it), or a
 * schedule item. Null when nothing changed.
 */
export function getCostsEditSave(row, oldRow, newId) {
  if (row.kind === "master") {
    if (row.costValue === oldRow.costValue) return null;
    const orphans = getOrphanScheduleItems(row, row.cost);
    return {
      BudgetCountyPracticeLineItems: [{ ...toLineItem(row.cost, newId), value: row.costValue }],
      ...(orphans.length ? { BudgetCountyPracticeScheduleItems: orphans } : {}),
    };
  }
  return SCHEDULE_EDIT_FIELDS.every((field) => row[field] === oldRow[field])
    ? null
    : {
        BudgetCountyPracticeScheduleItems: [
          { ...toScheduleItem(row), value: scheduleItemValue(row), is_active: true },
        ],
      };
}

/**
 * "+ add item" on a Costs screen row: a schedule item after the cost's last, and the cost where it's missing. The item
 * is named like the cost. A plain cost's first item is the cost itself at its $/acre.
 */
export function getAddScheduleItemSave(row, newId) {
  const { cost } = row;
  // BudgetMaster_Update sets a cost to its items' total, so this keeps a plain cost's $/acre.
  const storedName = budgetLineItemStoredName(cost.name);
  const costAsItem =
    cost.id && !cost.isScheduled && row.costValue
      ? {
          name: budgetLineItemLabel(cost.name),
          quantity: 1,
          unit_of_measure: "each",
          unit_value: row.costValue,
          value: row.costValue,
        }
      : {};
  return {
    ...(cost.id ? {} : { BudgetCountyPracticeLineItems: [{ ...toLineItem(cost, newId), value: 0 }] }),
    BudgetCountyPracticeScheduleItems: [
      ...getOrphanScheduleItems(row, cost),
      {
        id: newId(),
        budget_county_practice_id: row.id,
        category: cost.category,
        line_item_name: storedName,
        ordinal: (_.max(cost.scheduleItems.map((si) => num(si.ordinal))) ?? -1) + 1,
        name: budgetLineItemLabel(cost.name),
        quantity: 0,
        unit_of_measure: "gal",
        case_quantity: 0,
        case_unit_of_measure: "gal",
        unit_value: 0,
        value: 0,
        is_active: true,
        input_share_percent: cost.scheduleItems[0]?.input_share_percent ?? null,
        ...costAsItem,
      },
    ],
  };
}

const EDIT_LIMITS = {
  reported_acreage: [0, Infinity, "Acres must be 0 or more."],
  yield: [0, Infinity, "Yield must be 0 or more."],
  production_share_percent: [0, 100, "Share % must be between 0 and 100."],
  value: [-Infinity, Infinity, "Enter a $/acre amount."],
};

/** Why an edit can't be saved, or null. Only the values it sends are checked. */
export function getEditError(save) {
  for (const item of [...(save.BudgetCountyPractices ?? []), ...(save.BudgetCountyPracticeLineItems ?? [])]) {
    for (const [field, [min, max, message]] of Object.entries(EDIT_LIMITS)) {
      const value = item[field] === null || item[field] === "" ? NaN : Number(item[field]);
      if (field in item && !(Number.isFinite(value) && value >= min && value <= max)) return message;
    }
  }
  return null;
}

/** Deleting a cost: the cost and each of its schedule items, inactive, in one save. */
export function getDeleteCostSave(lineItem) {
  return {
    BudgetCountyPracticeLineItems: [
      { ..._.pick(lineItem, LINE_ITEM_FIELDS), name: budgetLineItemStoredName(lineItem.name), is_active: false },
    ],
    BudgetCountyPracticeScheduleItems: lineItem.scheduleItems.map((si) => ({
      ...toScheduleItem(si),
      is_active: false,
    })),
  };
}

/**
 * Changed schedule items (deleted ones arrive inactive) as one save. A cost the save leaves with no active items goes
 * with it at $0, as the county/crop page leaves it: BudgetMaster_Update recalculates a cost only while items remain.
 */
export function getScheduleSave(response, scheduleItems) {
  const BudgetCountyPracticeScheduleItems = scheduleItems.map(toScheduleItem);
  const scheduleKey = (si) => practiceCostKey(si.budget_county_practice_id, si.category, si.line_item_name);
  const remaining = new Set(upsertById(response?.schedule_items, BudgetCountyPracticeScheduleItems).map(scheduleKey));
  const emptied = new Set((response?.schedule_items ?? []).map(scheduleKey).filter((key) => !remaining.has(key)));
  const emptiedCosts = (response?.line_items ?? []).filter((li) =>
    emptied.has(practiceCostKey(li.budget_county_practice_id, li.category, li.name))
  );
  return {
    ...(emptiedCosts.length
      ? {
          BudgetCountyPracticeLineItems: emptiedCosts.map((li) => ({
            ..._.pick(li, LINE_ITEM_FIELDS),
            name: budgetLineItemStoredName(li.name),
            value: 0,
          })),
        }
      : {}),
    BudgetCountyPracticeScheduleItems,
  };
}

const updateById = (rows = [], changes = []) => {
  const changesById = new Map(changes.map((change) => [idKey(change.id), change]));
  return rows.map((row) => ({ ...row, ...changesById.get(idKey(row.id)), id: row.id }));
};

const upsertById = (rows = [], changes = []) => {
  const rowIds = new Set(rows.map((row) => idKey(row.id)));
  return [...updateById(rows, changes), ...changes.filter((change) => !rowIds.has(idKey(change.id)))].filter(
    (row) => row.is_active !== false
  );
};

/** What puts `response` back after a failed `save`: the old value of each sent field, and each new row inactive. */
export function getUndoSave(response, save) {
  const undo = (rows = [], changes = []) => {
    const rowsById = new Map(rows.map((row) => [idKey(row.id), row]));
    return changes.map((change) => {
      const row = rowsById.get(idKey(change.id));
      return row ? _.mapValues(change, (value, field) => row[field]) : { ...change, is_active: false };
    });
  };
  return {
    BudgetCountyPractices: undo(response?.county_practices, save.BudgetCountyPractices),
    BudgetCountyPracticeLineItems: undo(response?.line_items, save.BudgetCountyPracticeLineItems),
    BudgetCountyPracticeScheduleItems: undo(response?.schedule_items, save.BudgetCountyPracticeScheduleItems),
  };
}

/**
 * The BudgetMaster search response after a save, as BudgetMaster_Update leaves the database: rows upserted by id,
 * inactive ones dropped, and each schedule-driven cost the save touched set to its schedule total (summed, then
 * rounded once). Unlike the proc's isnull, a null county/crop field is applied, so a failed edit can revert to null.
 * County/crops are only updated, so a save that lands after a year or producer switch adds no partial row.
 */
export function applyBudgetMasterSave(response, save) {
  if (!response) return response;
  const schedule_items = upsertById(response.schedule_items, save.BudgetCountyPracticeScheduleItems);
  const totals = new Map(
    Object.entries(
      _.groupBy(schedule_items, (si) => practiceCostKey(si.budget_county_practice_id, si.category, si.line_item_name))
    ).map(([key, items]) => [key, scheduleTotal(items)])
  );
  const touched = new Set([
    ...(save.BudgetCountyPracticeLineItems ?? []).map((li) =>
      practiceCostKey(li.budget_county_practice_id, li.category, li.name)
    ),
    ...(save.BudgetCountyPracticeScheduleItems ?? []).map((si) =>
      practiceCostKey(si.budget_county_practice_id, si.category, si.line_item_name)
    ),
  ]);
  const line_items = upsertById(response.line_items, save.BudgetCountyPracticeLineItems).map((li) => {
    const key = practiceCostKey(li.budget_county_practice_id, li.category, li.name);
    return touched.has(key) && totals.has(key) ? { ...li, value: totals.get(key) } : li;
  });

  return {
    ...response,
    county_practices: updateById(response.county_practices, save.BudgetCountyPractices),
    line_items,
    schedule_items,
  };
}
